import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import useSWR from "swr";
import ReactGA from "react-ga4";
import fetch from "../../auth/FetchInterceptor.js";
import { END_POINTS } from "constants/APIConstant.js";
import { MutateCacheSWRPartialKey } from "services/SWRPartialKeyService.js";
import { profileFormSchema } from "./profileSchema";
import { normalizeBirthDateForApi } from "./utils";

export function useProfileForm() {
  const intl = useIntl();
  const dir = useSelector(({ theme }) => theme.direction);
  const { id } = useSelector(({ auth }) => auth);

  const { control, setValue, handleSubmit, formState } = useForm({
    resolver: yupResolver(profileFormSchema),
    defaultValues: {
      id,
    },
  });
  const { errors } = formState;

  const getData = useSWR({
    url: END_POINTS.GET_USER_DATA,
  });

  const [show, setShow] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const [buttonSpinner, setButtonSpinner] = useState(false);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => clearTimeout(timeOut);
  }, []);

  useEffect(() => {
    ReactGA.initialize(END_POINTS.TRACKING_ID);
    ReactGA.send({
      hitType: "pageview",
      page: "/profile",
      title: "Profile Page",
    });
  }, []);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      getData.mutate();
      const user = getData?.data?.data;
      if (user != null) {
        setValue("birthDate", user.birthDate?.slice(0, 10));
        setValue("phoneNumber", user.phoneNumber);
        setValue("firstName", user.firstName);
        setValue("lastName", user.lastName);
        setValue("address", user.address);
        setValue("email", user.email);
        setValue("username", user.username);
      }
    }, 0);
    return () => clearTimeout(timeOut);
  }, [getData?.data, getData.mutate, setValue]);

  const handleClose = () => setShow(false);

  async function updateData(formData) {
    setButtonSpinner(true);

    const payload = {
      id,
      firstName: formData.firstName,
      lastName: formData.lastName,
      phoneNumber: formData.phoneNumber,
      address: formData.address,
      birthDate: normalizeBirthDateForApi(formData.birthDate),
    };

    try {
      await fetch.put(END_POINTS.UPDATE_USER_DATA, payload);
      setShowMessage(intl.formatMessage({ id: "successfully-update" }));
      setSuccess(true);
      setButtonSpinner(false);
      setShow(true);
      setTimeout(() => {
        setShow(false);
        MutateCacheSWRPartialKey(END_POINTS.GET_USER_DATA);
      }, 2000);
    } catch (e) {
      setShowMessage(e.response?.data?.message);
      setSuccess(false);
      setButtonSpinner(false);
      setShow(true);
    }
  }

  const onSubmit = handleSubmit((formData) => {
    updateData(formData);
  });

  return {
    id,
    dir,
    control,
    errors,
    isLoading: getData.isLoading,
    onSubmit,
    buttonSpinner,
    snackbar: {
      open: show,
      onClose: handleClose,
      severity: success ? "success" : "error",
      message: showMessage,
    },
  };
}
